import type { TournamentWSClient } from '../sdk'
import type { TimeSeriesResponse } from '../sdk'
import { OperationStore, ReconciliationManager } from '../store'
import { CREATE_MOCK_PROVIDERS } from '@dx-private/dxchart5-react-mock-providers'
import type { MergedSymbolInfo } from './dx-trading-mappers'
import { chartDebugLog } from '../debug/browser-logging'
import { TournamentChartDataProvider } from './dx-data-provider'
import { TournamentOrderProvider } from './dx-order-provider'
import { TournamentPositionProvider } from './dx-position-provider'
import {
  StubSymbolSuggestProvider,
  TournamentTradingSessionsProvider,
} from './stub-providers'

// Force the trading resizer/pills to start at the left-most allowed bound.
// DXChart computes runtime x from chart width - settings.rightOffset.
// A sufficiently large rightOffset yields a negative x, which is clamped to left bound.
const TRADING_LEFT_START_RIGHT_OFFSET_PX = 10_000
export interface TournamentAggregationPeriod {
  duration: number
  durationType: string
}

export const AGGREGATION_PERIOD_1M: TournamentAggregationPeriod = {
  duration: 1,
  durationType: 'm',
}
export const AGGREGATION_PERIOD_5M: TournamentAggregationPeriod = {
  duration: 5,
  durationType: 'm',
}
export const ALLOWED_PERIODS: TournamentAggregationPeriod[] = [
  AGGREGATION_PERIOD_1M,
  AGGREGATION_PERIOD_5M,
  { duration: 15, durationType: 'm' },
  { duration: 1, durationType: 'h' },
  { duration: 2, durationType: 'h' },
]

export interface TournamentProvidersConfig {
  wsClient: TournamentWSClient
  store: OperationStore
  reconciliationManager: ReconciliationManager
  symbolShortNames: Record<string, string>
  symbolFullNames: Record<string, string>
  mergedSymbols: Record<string, MergedSymbolInfo>
  useDemoData?: boolean
  startDate?: string
  endDate?: string
  symbol?: string
  dataTimeoutMs?: number
  priceTypes?: string[]
  preloadedData?: TimeSeriesResponse
  tournamentType?: string
  isHistorical?: boolean
  localUserId?: string
}

export function createTournamentProviders(config: TournamentProvidersConfig): any {
  const {
    wsClient,
    store,
    symbolShortNames,
    symbolFullNames,
    mergedSymbols,
    useDemoData,
    startDate,
    endDate,
    symbol = 'EUR/USD',
    dataTimeoutMs,
    priceTypes,
    preloadedData,
    tournamentType,
    isHistorical,
    localUserId,
  } = config

  const isHistoricalMode = isHistorical === true
  const isLiveMode = !isHistorical

  const resolvedDataTimeoutMs =
    Number.isFinite(dataTimeoutMs) && Number(dataTimeoutMs) > 0 ? Number(dataTimeoutMs) : 30_000
  const resolvedPriceTypes = Array.isArray(priceTypes) && priceTypes.length > 0 ? priceTypes : ['last']

  // Resolve initialInstrument from symbol parameter
  const symbolFullName = symbolFullNames[symbol] || symbol
  chartDebugLog('tournament', 'resolved symbol to full name', { symbol, symbolFullName })

  // Real providers backed by store
  const chartDataProvider = new TournamentChartDataProvider(
    wsClient,
    symbolShortNames,
    symbolFullNames,
    store,
    { useDemoData, startDate, endDate, preloadedData, isHistorical }
  )

  const orderProvider = new TournamentOrderProvider(
    store,
    symbolFullNames,
    wsClient,
    mergedSymbols
  )

  const positionProvider = new TournamentPositionProvider(
    store,
    symbolFullNames,
    mergedSymbols
  )

  // Custom instrument provider for tournament symbols
  const symbolSuggestProvider = new StubSymbolSuggestProvider(
    symbolFullNames,
    symbolShortNames,
    mergedSymbols
  )

  // Tournament-aware trading sessions provider (forex 24x5: continuous REGULAR on weekdays, NO_TRADING on weekends)
  const tradingSessionsProvider = new TournamentTradingSessionsProvider(startDate, endDate)

  // Reuse official mock providers for non-tournament features to keep contracts aligned
  const mockProviders = CREATE_MOCK_PROVIDERS()

  // Return DX Charts dependency object
  const dependencies = {
    ...mockProviders,

    // Tournament-specific providers
    chartDataProvider,
    symbolSuggestProvider,
    tradingSessionsProvider,
    orderProvider,
    positionProvider,
    initialInstrument: symbolFullName,

    // Expose internal state for overlays (prefixed with __ to avoid conflicts)
    __TOURNAMENT_STORE__: store,
    __SYMBOL_FULL_NAMES__: symbolFullNames,
    __MERGED_SYMBOLS__: mergedSymbols,
    __TOURNAMENT_TYPE__: tournamentType,
    __IS_HISTORICAL__: isHistorical === true,
    __START_DATE__: startDate,
    __END_DATE__: endDate,
    __LOCAL_USER_ID__: localUserId,

    // Historical tournaments use 1m aggregation, locked to 1m only
    ...(isHistoricalMode && {
      initialAggregationPeriods: ALLOWED_PERIODS,
      initialAggregation: AGGREGATION_PERIOD_1M,
    }),

    // Live tournaments default to 5m but allow user selection
    ...(isLiveMode && {
      initialAggregationPeriods: ALLOWED_PERIODS,
      initialAggregation: AGGREGATION_PERIOD_5M,
    }),

    chartReactConfig: {
      timeframePresets: { mode: 'none' },
      dateFormatters: {
        // Must return a valid date-fns pattern string.
        // Empty string crashes date-fns format() in DXChart legend pipeline.
        legend: () => "' '",
      },
      dataTimeout: resolvedDataTimeoutMs,
      priceTypes: resolvedPriceTypes,
      trading: {
        enabled: true,
        addNewOrderEnabled: false,
        currency: '',
        rightOffset: TRADING_LEFT_START_RIGHT_OFFSET_PX,
        quantityPrecision: 1,
        quantityStep: 0.1,
        minOrderQuantity: 0.1,
        defaultOrderQuantity: 0.1,
        pnlPrecision: 2,
      },
      rightClickMenu: {
        backgroundMenu: {
          buyMarketButton: { enabled: false },
          sellMarketButton: { enabled: false },
          buyLimitButton: { enabled: false },
          sellLimitButton: { enabled: false },
        },
      },
      settingsTabs: {
        trading: {
          options: {
            tradingFromChart: {
              enabled: false,
            },
            showOrders: {
              enabled: false,
            },
            showPositions: {
              enabled: false,
            },
            showExecutedOrders: {
              enabled: false,
            },
          },
        },
      },
    },
    initialChartReactSettings: {
      legend: {
        showOHLC: false,
        showVolume: false,
        showPeriod: false,
        showInstrument: true,
      },
      trading: {
        visible: true,
        showOrders: true,
        showPositions: true,
        rightOffset: TRADING_LEFT_START_RIGHT_OFFSET_PX,
        executedOrders: {
          enabled: true,
          displayMode: 'bubbles',
        },
      },
      scale: {
        fit: {
          orders: true,
          positions: true,
        },
      },
    },
    initialChartConfig: {
      components: {
        yAxis: {
          labels: {
            settings: {
              lastPrice: {
                mode: 'line-label',
              },
              studies: {
                mode: 'label',
              },
              countdownToBarClose: {
                mode: 'label',
              },
            },
          },
        },
      },
    },
  }

  return dependencies
}
